// Third-party
import React, { PureComponent } from 'react';
import classNames from 'classnames';

// Namespaces
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';

// Components
import ProposalHeaderComponent, { ProposalHeaderInfos } from './ProposalHeaderComponent';
import ProposalFooterComponent from './ProposalFooterComponent';
import { formatLocalizedString } from '../../../../utils/i18n';

// Utils
import ProposalHTML, { ProposalHTMLProps } from '../views/ProposalHTML';
import { ProposalDAOInterface, ProposalDAOInterfaces, ProposalTextsDAOInterface } from '../../DAOs/ProposalDAO';
import { DAOManager } from '../../DAOs/DAOManager';
import { ProposalTemplateOptions } from '../../../../redux/modules/types/proposal-v2';
import { PROPOSAL_ORIENTATION } from '../../utils/constants';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';

type Props = {
  className?: string;
  proposal: ProposalNS.Proposal;
  pageStyles: React.CSSProperties;
  renderedSections: any[];
  pageIndex: number;
  numberOfPages: number;
  setToolbarOption: (key: string, option: ProposalToolbarOption) => void;
  proposalTemplateOptions: ProposalTemplateOptions;
  hideHeader: boolean;
  hideFooter: boolean;
  innerRef?: React.ForwardedRef<HTMLDivElement>;
};

class ProposalPageComponent extends PureComponent<Props> {
  renderCustomHeaderFooter(templateData: ProposalNS.ProposalTemplateDataElement, key: string) {
    const { proposal, pageIndex, numberOfPages } = this.props;

    const data: ProposalElementNS.ProposalDataElement = {
      ...templateData,
      pageOverflowIndex: 0,
      parent: undefined,
      elementType: ProposalElementNS.ProposalElementType.Data,
    };

    const dao: ProposalDAOInterface = DAOManager.getInstance().buildDAO(templateData.DAO.type, proposal, data);
    const localDAO: ProposalTextsDAOInterface | undefined = dao.getInterface<ProposalTextsDAOInterface>(ProposalDAOInterfaces.Texts);
    if (localDAO === undefined) {
      return null;
    }

    return React.createElement<ProposalHTMLProps>(ProposalHTML, {
      key,
      texts: localDAO.getTexts({
        pageOverflowIndex: data.pageOverflowIndex,
        pageNumber: pageIndex.toFixed(),
        pageNumberPadded2: pageIndex.toFixed().padStart(2, '0'),
        numberOfPages,
      }),
      className: (data.dataView as ProposalNS.HtmlViewOptions).className,
    });
  }

  renderHeader(headerInfos: ProposalHeaderInfos) {
    const { hideHeader, proposal } = this.props;
    const {
      template: { customHeader, themes, customDisclaimer },
      options: { locale },
    } = proposal;
    const {
      proposalTemplateOptions: {
        styleOptions,
        styleOptions: { banners },
      },
    } = this.props;

    if (hideHeader) {
      return null;
    }

    if (customHeader !== undefined && customHeader.content !== undefined) {
      return this.renderCustomHeaderFooter(customHeader.content, 'header');
    }

    const theme = themes.find(templateTheme => templateTheme.key === proposal.options.theme);
    const bannerStyles = theme && theme.useCSSBannerStyles ? undefined : banners;

    return (
      <ProposalHeaderComponent
        styleOptions={styleOptions}
        locale={locale}
        headerInfos={headerInfos}
        bannerStyles={bannerStyles}
        customDisclaimer={customDisclaimer && customDisclaimer.header}
      />
    );
  }

  renderFooter() {
    const { hideFooter, proposal } = this.props;
    const {
      template: { customFooter, themes, customDisclaimer },
    } = proposal;
    const {
      proposalTemplateOptions: {
        styleOptions: { banners, orientation },
      },
    } = this.props;

    if (hideFooter) {
      return null;
    }

    if (customFooter !== undefined && customFooter.content !== undefined) {
      return this.renderCustomHeaderFooter(customFooter.content, 'footer');
    }

    const theme = themes.find(templateTheme => templateTheme.key === proposal.options.theme);
    const bannerStyles = theme && theme.useCSSBannerStyles ? undefined : banners;

    const extraDisclaimers = customFooter && customFooter.extraDisclaimers;

    return (
      <ProposalFooterComponent
        locale={proposal.options.locale}
        orientation={orientation}
        bannerStyles={bannerStyles}
        customDisclaimer={customDisclaimer && customDisclaimer.footer}
        extraDisclaimers={extraDisclaimers}
      />
    );
  }

  render() {
    const {
      proposal,
      renderedSections,
      pageStyles,
      pageIndex,
      className,
      innerRef,
      proposalTemplateOptions: {
        styleOptions: { orientation },
      },
    } = this.props;
    const { locale } = proposal.options;
    const { themes, websiteUrl } = proposal.template;

    const theme = themes.find(templateTheme => templateTheme.key === proposal.options.theme);

    const headerInfos: ProposalHeaderInfos = {
      ...proposal.user,
      websiteUrl,
      profileImgUrl: proposal.user.imgUrl,
      clientName: proposal.contact !== undefined ? proposal.contact.fullName : undefined,
      proposalDate: proposal.date,
      comparisonName: proposal.comparisonName,
      logoUrl: theme && theme.logos && theme.logos.header ? formatLocalizedString(theme.logos.header, locale) : undefined,
    };

    const classes = classNames('proposal-page-component', className, {
      landscape: orientation && orientation === PROPOSAL_ORIENTATION.LANDSCAPE,
    });

    return (
      <div style={pageStyles} className={classNames(classes, `page-${pageIndex}`)} ref={innerRef}>
        <p className="visually-hidden">
          Page
          {pageIndex}
        </p>
        {this.renderHeader(headerInfos)}

        <div className="proposal-page-elements">{renderedSections}</div>

        {this.renderFooter()}
      </div>
    );
  }
}

export default React.forwardRef<HTMLDivElement, Props>((props, ref) => <ProposalPageComponent {...props} innerRef={ref} />);
